import {Box2, Matrix3x2, Matrix3x2Like, Size2Like, Vector2, Vector2Like} from "@cm/math"
import {Descriptor, MipLevelRegion, Region} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {
    shiftRangeIntoFirstWrapPeriod,
    WRAP_REGION_MARGIN,
} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/region-mapping-abstraction-experiments/region-helpers"
import {AddressMode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/basic-definitions"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/affine-transform/definition"
import {ImageOpParameters, EvaluatedImageOpParameters} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"

type Definition = typeof definition

export const determineDescriptor = (
    parameters: EvaluatedImageOpParameters<ImageOpParameters<Definition>>,
): {
    descriptor: Descriptor
    resultOffset: Vector2Like
} => {
    const resultBox = parameters.resultSize ? Box2.fromSize(parameters.resultSize) : computeResultSize(parameters.sourceImage.descriptor, parameters.transform)
    const resultOffset = parameters.options.compensateOffset
        ? {
              x: resultBox.x,
              y: resultBox.y,
          }
        : {
              x: 0,
              y: 0,
          }
    const descriptor: Descriptor = {
        width: resultBox.width,
        height: resultBox.height,
        channels: parameters.sourceImage.descriptor.channels,
    }
    return {
        descriptor,
        resultOffset,
    }
}

export const computeResultSize = (sourceSize: Size2Like, transform: Matrix3x2Like): Box2 => {
    const matrix = Matrix3x2.fromMatrix3x2Like(transform)
    const p00 = new Vector2(0, 0)
    const p10 = new Vector2(sourceSize.width, 0)
    const p01 = new Vector2(0, sourceSize.height)
    const p11 = new Vector2(sourceSize.width, sourceSize.height)
    const p00t = matrix.multiplyVector(p00)
    const p10t = matrix.multiplyVector(p10)
    const p01t = matrix.multiplyVector(p01)
    const p11t = matrix.multiplyVector(p11)
    const minX = Math.min(p00t.x, p10t.x, p01t.x, p11t.x)
    const maxX = Math.max(p00t.x, p10t.x, p01t.x, p11t.x)
    const minY = Math.min(p00t.y, p10t.y, p01t.y, p11t.y)
    const maxY = Math.max(p00t.y, p10t.y, p01t.y, p11t.y)
    return new Box2(Math.round(minX), Math.round(minY), Math.round(maxX - minX), Math.round(maxY - minY))
}

const INTERPOLATION_MARGIN = 1

export type ComputeSourceRegionArgs = {
    sourceSize: Size2Like // size at mip level 0
    region: Region // target region, in units of mipLevel
    mipLevel: number // mip level of the target region
    resultOffset: Vector2Like
    sourceImageMipLevel: number
    invTransform: Matrix3x2
    addressMode: AddressMode
}

export const computeSourceRegion = (args: ComputeSourceRegionArgs): MipLevelRegion => {
    const {sourceSize, region, mipLevel, resultOffset, sourceImageMipLevel, invTransform, addressMode} = args
    const mipLevelScale = 1 << mipLevel
    // scale region to mip level 0
    const scaledRegion = {
        x: region.x * mipLevelScale + resultOffset.x,
        y: region.y * mipLevelScale + resultOffset.y,
        width: region.width * mipLevelScale,
        height: region.height * mipLevelScale,
    }
    // project the region corners to the source image
    const corners = [
        new Vector2(scaledRegion.x, scaledRegion.y),
        new Vector2(scaledRegion.x + scaledRegion.width, scaledRegion.y),
        new Vector2(scaledRegion.x + scaledRegion.width, scaledRegion.y + scaledRegion.height),
        new Vector2(scaledRegion.x, scaledRegion.y + scaledRegion.height),
    ].map((corner) => invTransform.multiplyVector(corner))
    const isWrapping = addressMode === "wrap"
    const rangeX = projectRange(
        corners.map((corner) => corner.x),
        mipLevelScale,
        isWrapping ? sourceSize.width : undefined,
    )
    const rangeY = projectRange(
        corners.map((corner) => corner.y),
        mipLevelScale,
        isWrapping ? sourceSize.height : undefined,
    )
    const margin = isWrapping ? WRAP_REGION_MARGIN : INTERPOLATION_MARGIN
    const x = Math.floor(rangeX.min - margin)
    const y = Math.floor(rangeY.min - margin)
    return {
        x,
        y,
        width: Math.ceil(rangeX.max + margin) - x,
        height: Math.ceil(rangeY.max + margin) - y,
        mipLevel: sourceImageMipLevel,
    }
}

// range of the corner coordinates in units of the mip level; when wrapping it is shifted into the first wrap period of the source
const projectRange = (cornerCoordinates: number[], mipLevelScale: number, wrapSize: number | undefined) => {
    const range = {min: Math.min(...cornerCoordinates) / mipLevelScale, max: Math.max(...cornerCoordinates) / mipLevelScale}
    return wrapSize === undefined ? range : shiftRangeIntoFirstWrapPeriod(range.min, range.max, wrapSize / mipLevelScale)
}
